import React, { useMemo, useState } from "react";
import "./GuardiaTable.css";
import "./GuardiaVehicleTable.css";
import { Box, Typography, CircularProgress, IconButton, Tooltip } from "@material-ui/core";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import { TableWrapper, Table, TableHeader, TableBody, TableRow } from "./Table";
import { ExitButton, FechaCell } from "./GuardiaCells";
import { AsociadasCell, ChoferCell, PatenteChip, TipoCell, VerMasButton } from "./VehicleUnitCells";
import GuardiaVehicleCardMobile from "./GuardiaVehicleCardMobile";
import { driverName, VehicleInsideRow } from "./utils/vehicleInsideRow";
import VehicleDetailDialog from "../Dialog/VehicleDetailDialog";
import VehicleExitDialog from "../Dialog/VehicleExitDialog";
import EditAssignedDockDialog from "../Dialog/EditAssignedDockDialog";
import type { EditDockTarget } from "../Dialog/EditAssignedDockDialog";
import { getEntryTransportAuthorization } from "./utils/entryAuthorization";
import { useLogisticParkNavigation } from "../../../../views/LogisticPark/RegistroIngreso/hooks/useLogisticParkNavigation";
import {
  buildExitPrefill,
  ExitResponsable,
  ExitUnitSelection,
} from "../../../../views/LogisticPark/RegistroIngreso/utils/exitPrefillUtils";

const COLUMN_COUNT = 9;

type GuardiaVehicleTableProps = {
  rows: VehicleInsideRow[];
  /**
   * Nombre de carrocería por el que filtrar. `getVehiclesInside` no acepta este filtro y sólo
   * devuelve el nombre, así que se aplica acá sobre lo ya cargado.
   */
  vehicleTypeFilter?: string;
  isLoading?: boolean;
  /** Recarga las unidades luego de actualizar el dock desde el detalle. */
  onDockUpdated?: () => void;
};

const EmptyState = () => (
  <>
    <Typography variant="h6" color="textSecondary">
      No hay unidades para mostrar
    </Typography>
    <Typography variant="body2" color="textSecondary">
      Probá limpiar los filtros o buscar por patente, nombre o documento.
    </Typography>
  </>
);

/** Textos que entran justo en su columna: se truncan y el valor completo queda en el title. */
const TruncatedCell: React.FC<{ value?: string | null }> = ({ value }) =>
  value ? (
    <span className="gv-location" title={value}>
      {value}
    </span>
  ) : (
    <span className="gv-empty">—</span>
  );

/**
 * Control de stock de unidades dentro del complejo: una fila por unidad.
 *
 * Los datos vienen de `getVehiclesInside`, que ya resuelve el rol de cada unidad, si el chofer
 * sigue dentro y el estado de las unidades asociadas. Todas las que se listan están dentro: las que
 * salen desaparecen, así que no hay columna de estado —sería siempre "Dentro"—; el único aviso es
 * "Sin chofer", que va en la celda del chofer y pinta el borde de la fila.
 *
 * La salida no se registra acá: el modal junta quién retira y con qué unidades, y con eso se abre
 * el formulario de egreso, que es el que hace el `addEvent`.
 */
const GuardiaVehicleTable: React.FC<GuardiaVehicleTableProps> = ({
  rows,
  vehicleTypeFilter,
  isLoading,
  onDockUpdated,
}) => {
  const { navigateToRegistration } = useLogisticParkNavigation();
  const [detailRow, setDetailRow] = useState<VehicleInsideRow | null>(null);
  const [exitRow, setExitRow] = useState<VehicleInsideRow | null>(null);
  const [dockTarget, setDockTarget] = useState<EditDockTarget | null>(null);

  const units = useMemo(
    () =>
      vehicleTypeFilter ? rows.filter((row) => row.vehicleTypeName === vehicleTypeFilter) : rows,
    [rows, vehicleTypeFilter]
  );

  const handleExitConfirm = (responsable: ExitResponsable, selected: ExitUnitSelection[]) => {
    const row = exitRow;
    setExitRow(null);
    if (row) navigateToRegistration("exit", buildExitPrefill(responsable, row, selected));
  };

  const handleEditDock = (target: EditDockTarget) => {
    setDetailRow(null);
    setDockTarget(target);
  };

  const isEmpty = units.length === 0;

  return (
    <>
      {/* // MOBILE */}
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        {isLoading ? (
          <Box sx={{ p: 4, textAlign: "center", bgcolor: "#fff", borderRadius: "12px", mt: 2 }}>
            <CircularProgress size={40} />
          </Box>
        ) : isEmpty ? (
          <Box sx={{ p: 4, textAlign: "center", bgcolor: "#fff", borderRadius: "12px", mt: 2 }}>
            <EmptyState />
          </Box>
        ) : (
          units.map((row) => (
            <GuardiaVehicleCardMobile
              key={row.key}
              row={row}
              onVerMas={() => setDetailRow(row)}
              onSalida={() => setExitRow(row)}
              onEditDock={handleEditDock}
            />
          ))
        )}
      </Box>

      {/* // DESKTOP */}
      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <TableWrapper>
          <Table className="gt-table gv-table">
            <TableHeader>
              <TableRow>
                <th>FECHA ENTRADA</th>
                <th>TIPO</th>
                <th>PATENTE</th>
                <th>UNIDAD ASOCIADA</th>
                <th>INGRESANTE / CHOFER</th>
                <th>EMPRESA DE TRANSPORTE</th>
                <th>DOCK</th>
                <th>DETALLE</th>
                <th>ACCIÓN</th>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <td colSpan={COLUMN_COUNT}>
                    <Box
                      sx={{
                        p: 8,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CircularProgress size={50} />
                    </Box>
                  </td>
                </TableRow>
              ) : isEmpty ? (
                <TableRow>
                  <td colSpan={COLUMN_COUNT}>
                    <Box sx={{ p: 4, textAlign: "center" }}>
                      <EmptyState />
                    </Box>
                  </td>
                </TableRow>
              ) : (
                units.map((row) => {
                  const canEditDock = Boolean(
                    row.eventId && getEntryTransportAuthorization({ event: row.event } as any)
                  );

                  return (
                    <TableRow
                      key={row.key}
                      className={!row.driverInside ? "gv-row-alert" : undefined}
                    >
                    <td>
                      <FechaCell fecha={row.fecha} />
                    </td>
                    <td>
                      <TipoCell row={row} />
                    </td>
                    <td>
                      <PatenteChip patente={row.plate} />
                    </td>
                    <td>
                      <AsociadasCell asociadas={row.associatedVehicles} />
                    </td>
                    <td>
                      <ChoferCell row={row} />
                    </td>
                    <td>
                      <TruncatedCell value={row.transportCompanyName} />
                    </td>
                    <td>
                      <span className="gt-dock-cell">
                        <span>{row.dock ?? "-"}</span>
                        {canEditDock && (
                          <Tooltip title="Editar dock asignado">
                            <IconButton
                              className="dock-edit-button"
                              aria-label={`Editar dock asignado de ${row.plate}`}
                              size="small"
                              onClick={() =>
                                handleEditDock({
                                  eventId: row.eventId,
                                  nombre: driverName(row.driver) || "Transporte",
                                  transportCompanyName: row.transportCompanyName,
                                  patente: row.plate,
                                  fecha: row.fecha,
                                  dock: row.dock,
                                })
                              }
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </span>
                    </td>
                    <td>
                      <VerMasButton onClick={() => setDetailRow(row)} />
                    </td>
                    <td>
                      <ExitButton onClick={() => setExitRow(row)} />
                    </td>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableWrapper>
      </Box>

      <VehicleDetailDialog
        open={Boolean(detailRow)}
        row={detailRow}
        onClose={() => setDetailRow(null)}
        onEditDock={handleEditDock}
      />
      <EditAssignedDockDialog
        target={dockTarget}
        onClose={() => setDockTarget(null)}
        onUpdated={onDockUpdated}
      />
      <VehicleExitDialog
        open={Boolean(exitRow)}
        row={exitRow}
        allRows={rows}
        onClose={() => setExitRow(null)}
        onConfirm={handleExitConfirm}
      />
    </>
  );
};

export default GuardiaVehicleTable;
